import replace from 'lodash/replace'
import TagManager from 'react-gtm-module'
import { trackEcommerceType, trackItemType } from './types'
import { CategoriaNode, CollezioneNode } from '@gql/graphql'

const GTM_ID = process.env.GTM_ID

// per assicurare che gli eventi siano eseguiti sempre dopo il wSetup
let eventQueue = []
let isReady = false
let previousUrl = null

export const setTrackingReady = () => {
  isReady = false
}

export const initGTMdataLayer = () => {
  TagManager.initialize({
    gtmId: GTM_ID,
    dataLayerName: 'dataLayer',
  })
}

export const removeTags = (str) => {
  if (str === null || str === '') return ''
  else str = str?.toString()
  return str
    ?.replace(/(<([^>]+)>)/gi, '')
    .replace(' ', '_')
    .toLowerCase()
}

const sendGTMData = (data: any): void => {
  if (data?.event != 'wSetup' && data?.event != 'virtual_pageview') {
    // se non è ancora scattato il wSetup aggiungo l'evento alla coda
    if (!isReady) {
      eventQueue.push(data)
      return
    }
  }

  TagManager.dataLayer({
    dataLayer: data,
    dataLayerName: 'dataLayer',
  })
}

export const uniformData = (param: string) =>
  replace(replace(param, / /g, '_'), /-/g, '_').toLowerCase()

export const getPageType = (page, item, path) => {
  const pageChiave = page?.chiave || ''
  let pageType = 'other'

  if (pageChiave.includes('404')) pageType = 'error_page'
  else if (pageChiave.includes('homepage')) pageType = 'homepage'
  else if (item?.modelClass == 'categoria') pageType = 'listing'
  else if (item?.modelClass?.includes('collezione')) pageType = 'collezione'
  else if (item?.modelClass?.includes('colore')) pageType = 'colore'
  else if (item?.modelClass?.includes('progetto')) pageType = 'projects' // sia listing che detail
  else if (pageChiave.includes('ricerca')) pageType = 'search_results'
  else if (pageChiave.includes('brand')) pageType = 'brand' // sia listing che detail
  else if (pageChiave.includes('showroom')) pageType = 'showroom' // sia listing che detail
  else if (pageChiave.includes('download')) pageType = 'download_area'
  else if (item?.modelClass == 'soluzione') pageType = 'service'
  else if (page?.hreflangs?.find((h) => h.locale == 'it')?.href?.includes('/grandi-lastre'))
    pageType = 'grandi_lastre'
  else if (
    pageChiave.includes('news') ||
    pageChiave.includes('journal') ||
    pageChiave.includes('blog')
  )
    pageType = 'journal' // sia listing che detail

  return pageType
}

const getItemPage = (pageProps) => {
  // If the current object has a 'item' property, return it
  if (pageProps.hasOwnProperty('item')) {
    return pageProps.item
  }

  // Check if the current object is not 'pagina' and is an object
  for (let key in pageProps) {
    if (key !== 'pagina' && typeof pageProps[key] === 'object' && pageProps[key] !== null) {
      return pageProps[key]
    }
  }

  return null
}
const getPage = (pageProps) => {
  // If the current object has a 'pagina' property, return it
  if (pageProps.hasOwnProperty('pagina')) {
    return pageProps.pagina
  }

  // Otherwise, return 'pagina' from item object
  const item = getItemPage(pageProps)
  if (item?.hasOwnProperty('pagina')) {
    return item.pagina
  }

  return null
}

const getBrand = (page, item) => {
  const brand = item?.modelClass === 'brand' ? item : item?.brand || item?.collezione?.brand || null
  return brand?.parent ? brand?.parent?.chiave : brand?.chiave
}

const getSubBrand = (page, item) => {
  const brand = item?.modelClass === 'brand' ? item : item?.brand || item?.collezione?.brand || null
  return brand?.parent ? brand?.chiave : null
}

const getListMacroCategory = (page, item) => {
  if (item?.modelClass == 'categoria') {
    // solo per listing
    return item?.filtriSlug?.length > 0 ? 'premium' : 'generico'
  }
  return null
}

const getListCategory = (page, item) => {
  if (item?.modelClass == 'categoria') {
    // solo per listing
    return item?.attributoModelloDefault || 'collezione'
  }
  return null
}

const getGTMPageProps = (paginaCorrente, router) => {
  const page = getPage(paginaCorrente)
  const item = getItemPage(paginaCorrente)
  const pagePath = router?.asPath

  const pageType = getPageType(page, item, pagePath)
  const brand = getBrand(page, item)
  const subBrand = getSubBrand(page, item)
  const listMacroCategory = getListMacroCategory(page, item)
  const listCategory = getListCategory(page, item)

  return {
    page_type: pageType,
    brand: uniformData(brand),
    sub_brand: uniformData(subBrand),
    list_macrocategory: uniformData(listMacroCategory), // generico, premium (solo listing page)
    list_category: uniformData(listCategory), // colore, collezione (solo listing page)
  }
}

let currentPage = {}
let currentChannel = ''

export const setPage = (page, locale) => {
  currentPage = page
  currentChannel = locale
}

export const trackingGAevent = (eventName, values = {}) => {
  // const path = getPagePath(currentPage)

  const data = {
    event: 'ga4_event',
    event_name: eventName || '',
    ...values,
  }

  sendGTMData(data)
}

export const trackingEcommerceEvent = (event, values: trackEcommerceType) => {
  const { items, ...restValues } = values
  const chunkSize = 12

  // If items are less than or equal to chunk size, send data directly
  if (items.length <= chunkSize) {
    sendGTMData({ event: event || '', ecommerce: values })
    return
  }

  // Split items into chunks and send each chunk
  for (let i = 0; i < items.length; i += chunkSize) {
    const chunk = items.slice(i, i + chunkSize)
    sendGTMData({
      event: event || '',
      ecommerce: { ...restValues, items: chunk },
    })
  }
}

export const updateGTMDataLayer = (paginaCorrente, router) => {
  const gtmPageProps = getGTMPageProps(paginaCorrente, router)
  sendGTMData({
    event: 'wSetup',
    environment:
      process.env.NODE_ENV === 'development' || process.env.TEMP_ENV === 'staging'
        ? 'staging'
        : 'production', // staging, production
    country: router.locale, // IT, FR DE, ...
    language: router.locale, // IT, EN, FR, DE, ES, RU
    ...gtmPageProps,
  })

  sendGTMData({
    event: 'virtual_pageview',
    page_location: document.location.href,
    page_referrer: !previousUrl ? document?.referrer || '' : previousUrl,
  })

  isReady = true
  previousUrl = document.location.href

  // dopo il wSetup eseguo tutti gli eventi della coda
  while (eventQueue.length > 0) {
    const eventData = eventQueue.shift()
    sendGTMData(eventData)
  }
}

export const collezioneToTrack = (
  collezione: any,
  index?: number,
  categoria?: any
): trackItemType => {
  if (collezione) {
    if (categoria) {
      localStorage.setItem('collezioneToTrackCategoria', JSON.stringify(categoria))
    } else {
      const categoriaStr = localStorage.getItem('collezioneToTrackCategoria')
      if (categoriaStr) {
        categoria = JSON.parse(categoriaStr)
      }
    }
    return {
      item_id: collezione?.pk || collezione?.id,
      item_name: uniformData(collezione.slug),
      item_brand: uniformData(collezione?.brand?.nome) || '',
      item_category: uniformData(collezione?.categoriaEffetto?.slug) || '',
      ...(categoria ? { item_list_id: categoria?.pk || 0 } : {}),
      ...(categoria
        ? { item_list_name: categoria?.slug || uniformData(categoria?.nome) || '' }
        : {}),
      ...(index !== undefined ? { index } : {}),
      quantity: 1,
    }
  }
  return null
}
